/* eslint-disable @typescript-eslint/no-misused-promises */
// useCompsData.ts

import { useCallback, useMemo, useState } from "react"
import { useStore } from "@nanostores/react"
import {
  currentSortingState,
  isCurrentSortReversed,
} from "@/stores/compSortingDataStore"
import { type ArvCompType, type PropertyType } from "@/generated/graphql"
import { type PrimarySortCriterion } from "@/types/compTypes"
import { useUpdateCompSelectionMutation } from "@/queries/analysis"
import { queryClient } from "@/queries/client"
import {
  compsDataStore,
  $selectedCompDetailId,
  setCompUpdatingState,
  toggleSelectedComp,
} from "@/stores/compsDataStore"
import { parseCoordinate } from "@/utils/NumberUtils"
import { calculatedDistance, extractPrice } from "@/utils/CompUtils"

const useCompsData = (subjectProperty?: PropertyType | null) => {
  const sortState = useStore(currentSortingState)
  const primaryCriterion: PrimarySortCriterion =
    (sortState.currentCriterion as PrimarySortCriterion) || "suggested"
  const selectedId: string | null = useStore($selectedCompDetailId)
  const updateCompSelection = useUpdateCompSelectionMutation()
  const [compDetailsMap, setCompDetailsMap] = useState<
    Record<string, ArvCompType>
  >({})
  const isReversed = useStore(isCurrentSortReversed)

  const { compsArray } = useStore(compsDataStore)

  const sortedComps = useMemo(() => {
    let comps = [...compsArray].sort((comp1, comp2) => {
      // Extract relevant properties for sorting
      const isSaleOrSold1: boolean = ["sale", "sold"].includes(
        comp1.arvcomp?.status?.toLowerCase() ?? ""
      )
      const isSaleOrSold2: boolean = ["sale", "sold"].includes(
        comp2.arvcomp?.status?.toLowerCase() ?? ""
      )
      const date1 = comp1.arvcomp?.dateSold
        ? new Date(comp1.arvcomp.dateSold).getTime()
        : 0
      const date2 = comp2.arvcomp?.dateSold
        ? new Date(comp2.arvcomp.dateSold).getTime()
        : 0
      const ppsf1 = comp1.arvcomp?.pricePerSqft || 0
      const ppsf2 = comp2.arvcomp?.pricePerSqft || 0
      const beds1 = comp1.arvcomp?.bedrooms || 0
      const beds2 = comp2.arvcomp?.bedrooms || 0
      const baths1 = comp1.arvcomp?.bathrooms || 0
      const baths2 = comp2.arvcomp?.bathrooms || 0
      const originalIndex1 = comp1.originalIndex ?? Number.MAX_VALUE
      const originalIndex2 = comp2.originalIndex ?? Number.MAX_VALUE
      const pricingPrice1: number | null | undefined =
        extractPrice(comp1.arvcomp?.pricing) || comp1.arvcomp?.soldPrice || 0
      const pricingPrice2: number | null | undefined =
        extractPrice(comp2.arvcomp?.pricing) || comp2.arvcomp?.soldPrice || 0
      const distance1: number | null =
        calculatedDistance(
          parseCoordinate(subjectProperty?.latitude),
          parseCoordinate(subjectProperty?.longitude),
          parseCoordinate(comp1.arvcomp?.latitude),
          parseCoordinate(comp1.arvcomp?.longitude)
        ) || 0
      const distance2: number | null =
        calculatedDistance(
          parseCoordinate(subjectProperty?.latitude),
          parseCoordinate(subjectProperty?.longitude),
          parseCoordinate(comp2.arvcomp?.latitude),
          parseCoordinate(comp2.arvcomp?.longitude)
        ) || 0
      const yearBuilt1 = comp1.arvcomp?.yearBuilt || 0
      const yearBuilt2 = comp2.arvcomp?.yearBuilt || 0
      const sqft1 = comp1.arvcomp?.area || 0
      const sqft2 = comp2.arvcomp?.area || 0
      const dom1 = comp1.arvcomp?.daysOnMarket || 0
      const dom2 = comp2.arvcomp?.daysOnMarket || 0

      switch (primaryCriterion) {
        case "suggested":
          return originalIndex1 - originalIndex2
        case "distance":
          return distance1 - distance2
        case "soldDate": {
          if (!isSaleOrSold1 && isSaleOrSold2) return 1 // Comp1 is not sold but Comp2 is, move Comp1 down
          if (isSaleOrSold1 && !isSaleOrSold2) return -1 // Comp2 is not sold but Comp1 is, move Comp2 down
          return date2 - date1
        }
        case "pricePerSqft":
          return ppsf2 - ppsf1
        case "beds":
          return beds1 - beds2
        case "baths":
          return baths1 - baths2
        case "yearBuilt":
          return yearBuilt2 - yearBuilt1
        case "squareFeet":
          return sqft2 - sqft1
        case "soldPrice":
          return pricingPrice2 - pricingPrice1
        case "daysOnMarket":
          return dom2 - dom1
        case "updatedAt": {
          const updatedAt1 = comp1.arvcomp?.updatedAt // TODO: use comp1?.updatedAt when BE makes available
            ? new Date(comp1.arvcomp.updatedAt).getTime()
            : 0
          const updatedAt2 = comp2.arvcomp?.updatedAt
            ? new Date(comp2.arvcomp.updatedAt).getTime()
            : 0
          return updatedAt2 - updatedAt1 // Newest to oldest
        }
        default:
          return originalIndex1 - originalIndex2
      }
    })
    if (isReversed) {
      comps = comps.reverse()
    }
    return comps
  }, [compsArray, sortState, isReversed])

  const addCompDetail = useCallback((compId: string, details: ArvCompType) => {
    setCompDetailsMap((prevDetails) => ({ ...prevDetails, [compId]: details }))
  }, [])

  const sortedCompsWithDetails = useMemo(
    () =>
      sortedComps.map((comp) => {
        const details = compDetailsMap[comp.arvcomp?.id!]
        return details ? { ...comp, arvcomp: details } : comp
      }),
    [compsArray, sortedComps, compDetailsMap]
  )

  // Memoized value for sorted comps with 'selectedFirst' prioritized
  const sortedCompsWithSelectedFirst = useMemo(() => {
    const startsWithSelected: boolean =
      typeof sortState.currentCriterion === "string" &&
      sortState.currentCriterion.startsWith("selectedFirst")
    // console.log(`* startsWithSelected/currentCriterion - ${startsWithSelected} ${sortState.currentCriterion} ${sortState.currentCriterion.toString()}`)
    if (!startsWithSelected) {
      return sortedComps // Return the original sorted list if 'selectedFirst' is not part of the sort criterion
    }
    // Prioritize selected comps in sorting
    return [...sortedComps].sort((comp1, comp2) => {
      return comp1.isSelected === comp2.isSelected
        ? 0
        : comp1.isSelected
          ? -1
          : 1
    })
  }, [compsArray, sortedComps, sortState, primaryCriterion])

  const selectedCompCount = compsArray.filter((comp) => comp.isSelected).length
  const totalCompCount = compsArray.length
  const selectedCompIds = compsArray
    .filter((comp) => comp.isSelected)
    .map((comp) => comp.arvcomp?.id)

  // Memoized value for only selected comps
  const selectedComps = useMemo(() => {
    return compsArray.filter((comp) => comp.isSelected)
  }, [compsArray, sortedComps])

  const handleFirstSelection = useCallback(
    (analysisId: string, toggleCompId: string) => {
      // First, mark all comps as not selected
      const resetComps = compsArray.map((comp) => ({
        ...comp,
        isSelected: false,
      }))

      // Then, find and activate the specified comp
      const updatedComps = resetComps.map((comp) =>
        comp.arvcomp?.id === toggleCompId ? { ...comp, isSelected: true } : comp
      )

      // Extract IDs of selected comps for mutation
      const selectedCompIds = updatedComps
        .filter((comp) => comp.isSelected)
        .map((comp) => comp.arvcomp?.id) as string[]

      // console.log(`*handleFirstSelection- ${selectedCompIds.toString()}`)

      // Perform the mutation with the selected comp IDs
      updateCompSelection.mutate(
        { analysisId, selectedCompIds },
        {
          onSuccess: (data) => {
            console.log(
              "Successfully updated comp selection for first-time selection:",
              data
            )
          },
          onError: (error) => {
            console.error(
              "Error updating comp selection for first-time selection",
              error
            )
          },
          onSettled: async () => {
            await queryClient.invalidateQueries({
              queryKey: ["analysis", analysisId],
            })
            setCompUpdatingState(false) // This will wait until invalidation is complete
          },
        }
      )

      // Update the state to reflect only the selected comp
      setCompDetailsMap((prevDetails) => {
        const newDetails = { ...prevDetails }
        Object.keys(newDetails).forEach((id) => {
          if (id !== toggleCompId) {
            newDetails[id] = { ...newDetails[id], isSelected: false }
          }
        })
        return newDetails
      })
    },
    [compsArray, updateCompSelection.mutate]
  )

  const handleToggleSelection = useCallback(
    (analysisId: string, toggledCompId: string) => {
      const updatedComps = compsArray.map((comp) =>
        comp.arvcomp?.id === toggledCompId
          ? { ...comp, isSelected: !comp.isSelected }
          : comp
      )
      toggleSelectedComp(toggledCompId)

      const selectedCompIds = updatedComps
        .filter((comp) => comp.isSelected)
        .map((comp) => comp.arvcomp?.id) as string[]

      updateCompSelection.mutate(
        { analysisId, selectedCompIds },
        {
          onSuccess: (data) => {
            console.log("Successfully updated comp selection:", data)
          },
          onError: (error) => {
            console.error("Error updating comp selection", error)
          },
          onSettled: async () => {
            await queryClient.invalidateQueries({
              queryKey: ["analysis", analysisId],
            })
            setCompUpdatingState(false) // This will wait until invalidation is complete
          },
        }
      )
    },
    [compsArray, updateCompSelection.mutate]
  )

  // Calculate average only if there are selected comps, otherwise default to 0
  const averagePricePerSqft =
    selectedCompCount > 0
      ? selectedComps.reduce(
          (total, comp) => total + (comp.arvcomp?.pricePerSqft || 0),
          0
        ) / selectedCompCount
      : 0

  const selectedCompDetail = useMemo(() => {
    return compsArray.find((comp) => comp.arvcomp?.id === selectedId) || null
  }, [compsArray, selectedId])

  return {
    selectedCompCount,
    totalCompCount,
    addCompDetail,
    selectedCompIds,
    selectedComps, // New list with only selected comps
    sortedComps, // Original sorted list
    sortedCompsWithSelectedFirst, // New list with 'selectedFirst' sorting
    sortedCompsWithDetails, // Additional details
    handleFirstSelection,
    handleToggleSelection,
    averagePricePerSqft,
    selectedCompDetail,
    calculatedDistance,
    sortCriterion: primaryCriterion,
  }
}

export default useCompsData
